import React, { Component } from 'react';
import { unitLabel, categoryLabel } from '../utils/supply';

var config = process.env;

// Receta de insumos de un ítem de menú (ENI-82/ENI-90): qué insumos descuenta cada venta y
// cuánto (en la unidad base del insumo). La cantidad se pre-carga con el "consumo típico"
// (defaultConsumption) del insumo.
//
// ENI-192: se listan TODOS los insumos, no sólo los fraccionables. Uno de unidad entera
// (servilletas, una lata) se descuenta por unidad — el backend ya lo soportaba.
//
// Dos modos:
//  - Descontrolado (edición): recibe `gastronomyCode`, carga la receta y la guarda con PUT.
//  - Controlado (alta, ENI-90): recibe `value` + `onChange`; NO carga ni guarda receta,
//    emite las líneas hacia arriba para que el alta las mande en el body del POST.
function headers() {
    return {
        'Content-Type': 'application/json',
        'access-token': localStorage.getItem('token'),
    };
}

class CafeteriaRecipeComponent extends Component {
    constructor(props) {
        super(props);
        this.controlled = typeof props.onChange === 'function';
        this.state = {
            loading: true,
            saving: false,
            supplies: [], // insumos disponibles: fraccionables Y de unidad entera (ENI-192)
            lines: this.controlled ? (props.value || []) : [], // [{ supplyCode, quantity }]
            newSupplyCode: '',
        };
    }

    componentDidMount() {
        this.fetchAll();
    }

    fetchAll = () => {
        const base = config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
        const requests = [
            fetch(base + 'cafeteria/supplies', { method: 'GET', headers: headers() }).then((r) => r.json()),
        ];
        // En modo controlado (alta) no hay ítem todavía: las líneas vienen por props.
        if (!this.controlled) {
            requests.push(
                fetch(base + `cafeteria/supplies/recipe/${encodeURIComponent(this.props.gastronomyCode)}`, { method: 'GET', headers: headers() }).then((r) => r.json())
            );
        }
        Promise.all(requests).then(([suppliesData, recipeData]) => {
            if (suppliesData.error > 0 || (recipeData && recipeData.error > 0)) {
                alert(suppliesData.message || (recipeData && recipeData.message));
                this.setState({ loading: false });
                return;
            }
            // ENI-192: sin filtro por isConsumable — se ofrecen todos los insumos.
            const supplies = (suppliesData.data || []);
            const next = { supplies, loading: false };
            if (!this.controlled) {
                next.lines = (recipeData.data || []).map((r) => ({ supplyCode: r.supplyCode, quantity: r.quantity }));
            }
            this.setState(next);
        }).catch(() => {
            alert('Error de red al cargar la receta.');
            this.setState({ loading: false });
        });
    }

    supplyByCode = (code) => this.state.supplies.find((s) => String(s.code) === String(code));

    // Actualiza las líneas y, en modo controlado, las emite hacia arriba.
    setLines = (lines) => {
        this.setState({ lines });
        if (this.controlled) {
            this.props.onChange(lines);
        }
    }

    addLine = () => {
        const code = this.state.newSupplyCode;
        if (code === '') {
            return;
        }
        if (this.state.lines.some((l) => String(l.supplyCode) === String(code))) {
            alert('Ese insumo ya está en la receta.');
            return;
        }
        const supply = this.supplyByCode(code);
        this.setState({ newSupplyCode: '' });
        this.setLines([...this.state.lines, {
            supplyCode: supply.code,
            quantity: supply.defaultConsumption != null ? supply.defaultConsumption : '',
        }]);
    }

    removeLine = (code) => {
        this.setLines(this.state.lines.filter((l) => String(l.supplyCode) !== String(code)));
    }

    updateQuantity = (code, value) => {
        this.setLines(this.state.lines.map((l) => (String(l.supplyCode) === String(code) ? { ...l, quantity: value } : l)));
    }

    save = () => {
        const lines = this.state.lines.map((l) => ({ supplyCode: l.supplyCode, quantity: Number(l.quantity) }));
        if (lines.some((l) => !(l.quantity > 0))) {
            alert('Toda línea de la receta necesita una cantidad > 0.');
            return;
        }
        this.setState({ saving: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/supplies/recipe/${encodeURIComponent(this.props.gastronomyCode)}`, {
            method: 'PUT', headers: headers(), body: JSON.stringify(lines),
        })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ saving: false });
                } else {
                    this.setState({ saving: false });
                    alert('Receta guardada.');
                }
            })
            .catch(() => {
                alert('Error de red al guardar la receta.');
                this.setState({ saving: false });
            });
    }

    render() {
        if (this.state.loading) {
            return (<div className={this.controlled ? '' : 'card card-small mb-4'}><div className={this.controlled ? '' : 'card-body p-4'}>Cargando receta…</div></div>);
        }
        const available = this.state.supplies.filter(
            (s) => !this.state.lines.some((l) => String(l.supplyCode) === String(s.code))
        );
        const body = (
            <React.Fragment>
                {/* ENI-110: encabezado con el conteo, y la lista como tabla — antes eran filas
                    sueltas sin jerarquía y el selector quedaba flotando abajo. */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                    <h6 className="m-0">Receta de insumos</h6>
                    <small className="text-muted">{this.state.lines.length} insumo{this.state.lines.length === 1 ? '' : 's'}</small>
                </div>
                <table className="table table-sm mb-2">
                    <thead>
                        <tr>
                            <th className="border-0"><small>Insumo</small></th>
                            <th className="border-0"><small>Categoría</small></th>
                            <th className="border-0"><small>Cantidad por venta</small></th>
                            <th className="border-0"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.lines.length === 0 && (
                            <tr><td colSpan="4" className="text-muted">Sin insumos: este ítem no descuenta nada al venderse.</td></tr>
                        )}
                        {this.state.lines.map((line) => {
                            const supply = this.supplyByCode(line.supplyCode);
                            const unit = unitLabel(supply);
                            const porUnidad = supply && !(Number(supply.packageSize) > 1);
                            return (
                                <tr key={line.supplyCode}>
                                    <td className="align-middle">
                                        {supply ? supply.name : line.supplyCode}
                                        {porUnidad && <span className="badge badge-secondary ml-1">Por unidad</span>}
                                    </td>
                                    <td className="align-middle text-muted">
                                        <small>{supply ? categoryLabel(supply.category) : '—'}</small>
                                    </td>
                                    <td className="align-middle" style={{ maxWidth: '180px' }}>
                                        <div className="input-group input-group-sm">
                                            <input type="number" className="form-control" min="0" step={porUnidad ? '1' : 'any'} value={line.quantity} disabled={this.state.saving} onChange={(e) => this.updateQuantity(line.supplyCode, e.target.value)} />
                                            <div className="input-group-append"><span className="input-group-text">{unit}</span></div>
                                        </div>
                                    </td>
                                    <td className="align-middle text-right">
                                        <button type="button" className="btn btn-sm btn-white" disabled={this.state.saving} onClick={() => this.removeLine(line.supplyCode)}>Quitar</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {this.state.supplies.length === 0 ? (
                    <div className="alert alert-warning mt-3 mb-0">
                        Todavía no hay insumos cargados. <a href="/cafeteriaInsumos">Creá un insumo</a> (café, leche, una bebida…) para poder armar la receta de este ítem.
                    </div>
                ) : (
                    <div className="form-row align-items-end">
                        <div className="form-group col-md-6 mb-2">
                            <label><small className="text-muted">Agregar insumo</small></label>
                            <select className="custom-select custom-select-sm" value={this.state.newSupplyCode} disabled={this.state.saving} onChange={(e) => this.setState({ newSupplyCode: e.target.value })}>
                                <option value="">Elegir…</option>
                                {/* Agrupado por categoría: con la lista completa (ENI-192) un
                                    desplegable plano se vuelve difícil de recorrer. */}
                                {[...new Set(available.map((s) => s.category))].map((cat) => (
                                    <optgroup key={cat || 'other'} label={categoryLabel(cat)}>
                                        {available.filter((s) => s.category === cat).map((s) => (
                                            <option key={s.code} value={s.code}>{s.name} ({unitLabel(s)})</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        <div className="form-group col-md-3 mb-2">
                            <button type="button" className="btn btn-sm btn-primary" onClick={this.addLine} disabled={this.state.saving || this.state.newSupplyCode === ''}>Agregar</button>
                        </div>
                    </div>
                )}
                <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    Cada venta de este ítem descuenta automáticamente estos insumos. Al anular, se puede elegir si vuelven al stock.
                    {this.controlled && ' Un ítem consumible por receta no se puede vender hasta tener al menos un insumo.'}
                </p>
                {!this.controlled && (
                    <React.Fragment>
                        <hr />
                        <button type="button" className="btn btn-accent" disabled={this.state.saving} onClick={this.save}>Guardar receta</button>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
        // En modo controlado se embebe dentro del form del ítem (sin card propia).
        if (this.controlled) {
            return (<div className="mt-2">{body}</div>);
        }
        return (<div className="card card-small mb-4"><div className="card-body p-4">{body}</div></div>);
    }
}

export default CafeteriaRecipeComponent;
